import { clsx } from 'clsx';
import { CSSProperties, FocusEvent, forwardRef, Ref } from 'react';
import {
  Text as AriaText,
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  FieldError,
  Label,
  TextArea,
} from 'react-aria-components';
import fieldStyles from '~/components/inputs/Field.module.scss';
import styles from './TextField.module.scss';

interface Props {
  autoComplete?: AriaTextFieldProps['autoComplete'];
  autoFocus?: boolean;
  className?: string;
  errorMessage?: string;
  helpText?: string;
  hideLabel?: boolean;
  id?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label?: string;
  name?: string;
  onBlur?: (e: FocusEvent<Element, Element>) => void;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  size?: 'small' | 'medium' | 'large';
  style?: CSSProperties;
  value: string;
  TextAreaProps?: {
    maxLength?: number;
    className?: string;
    style?: CSSProperties;
  };
}

const TextAreaField = forwardRef(
  (
    {
      autoComplete,
      autoFocus,
      className,
      errorMessage,
      helpText,
      hideLabel,
      id,
      isDisabled,
      isInvalid,
      isRequired,
      label,
      name,
      onBlur,
      onChange,
      placeholder,
      rows,
      size = 'medium',
      style,
      value,
      TextAreaProps,
    }: Props,
    ref?: Ref<HTMLDivElement>,
  ) => {
    return (
      <AriaTextField
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        aria-label={hideLabel ? label : undefined}
        className={clsx(fieldStyles.field, styles.inputField, className)}
        data-size={size}
        id={id}
        isInvalid={isInvalid || !!errorMessage}
        isDisabled={isDisabled}
        isRequired={isRequired}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        style={style}
        ref={ref}
        value={value}
      >
        {!hideLabel && label && (
          <Label>
            {label} {isRequired && ' *'}
          </Label>
        )}
        <div className={styles.inputWrapper}>
          <TextArea
            rows={rows}
            placeholder={placeholder}
            className={TextAreaProps?.className}
            maxLength={TextAreaProps?.maxLength}
            style={TextAreaProps?.style}
          />
        </div>
        {helpText && <AriaText slot="description">{helpText}</AriaText>}
        <FieldError>{errorMessage}</FieldError>
      </AriaTextField>
    );
  },
);

export default TextAreaField;
